/* ── Formation Activation Burst ──────────────────────────────────
 * Visceral feedback when a card activates: a colored particle fan
 * radiating outward from the swarm centroid, paired with a ground
 * ring that expands and fades over ~700ms.
 *
 * Follows the renderer convention used elsewhere in this folder:
 *   - constructor adds meshes to the scene
 *   - update(deltaMs) animates active slots
 *   - dispose() releases geometries/materials
 *
 * Pooled — N=8 burst slots round-robin through `spawn()`. Each slot
 * owns one particle Points object and one ground ring mesh. When a
 * slot is reused mid-life, its existing animation simply restarts.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec2, vec3, vec4, float, length, smoothstep } from 'three/tsl';
import { setEmissiveNode } from './tslUtils';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

const PARTICLE_COUNT = 32;
const POOL_SIZE = 8;
/** Burst duration in seconds — both particles and ring fade over this window. */
const LIFE_SECONDS = 0.7;
/** Final radius the ground ring reaches at end of life. */
const RING_MAX_SCALE = 12;
/** Y offset above the centroid for the flat ring (avoids terrain z-fighting). */
const RING_GROUND_OFFSET = 0.5;

interface BurstSlot {
  points: THREE.Points;
  pointsMaterial: THREE.PointsNodeMaterial;
  pointsGeometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  ring: THREE.Mesh;
  ringMaterial: THREE.MeshBasicNodeMaterial;
  uProgress: { value: number };
  uColor: { value: THREE.Color };
  active: boolean;
  life: number;
}

function buildParticleNodes(
  uProgress: ReturnType<typeof uniform>,
  uColor: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    const p = float(uProgress);
    // Particles start large (~50px) and shrink as they age.
    const shrink = float(1.0).sub(p.mul(0.6));
    return float(50.0).mul(shrink);
  })();

  const colorNode = Fn(() => {
    // Begin near white-hot, fade toward the card tint, fade alpha to zero.
    const tint = vec3(uColor);
    const rgb = tint.add(vec3(1.0, 1.0, 1.0).sub(tint).mul(float(uProgress).oneMinus().mul(0.6)));
    const alpha = float(uProgress).oneMinus();
    return vec4(rgb, alpha);
  })();

  return { sizeNode, colorNode };
}

function buildRingNodes(
  uProgress: ReturnType<typeof uniform>,
  uColor: ReturnType<typeof uniform>,
) {
  const colorFn = Fn(() => {
    // RingGeometry uvs span 0..1 on the radial axis. Brighten the outer
    // edge so the ring reads as an expanding shockwave, not a filled disc.
    const u = uv();
    const radial = length(u.sub(vec2(0.5, 0.5))).mul(2.0);
    const edge = smoothstep(float(0.4), float(1.0), radial);
    const rgb = vec3(uColor).add(vec3(0.2, 0.2, 0.2).mul(edge));
    const fade = float(uProgress).oneMinus();
    const alpha = edge.mul(fade).mul(0.65);
    return vec4(rgb, alpha);
  });
  return { colorNode: colorFn() };
}

/**
 * Pooled particle + ground-ring effect spawned on every `CardActivated`
 * server event. Owned by EffectsManager; pump `update()` from the same
 * frame loop that drives EffectsManager.
 */
export class FormationActivationBurst {
  private readonly scene: THREE.Scene;
  private readonly pool: BurstSlot[] = [];
  private cursor = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    for (let i = 0; i < POOL_SIZE; i++) {
      this.pool.push(this.createSlot());
    }
  }

  /**
   * Spawn a burst at `centroid` colored by `cardType`. `tintColor` should
   * come from the caller's palette (CARD_ACTIVATION_COLORS); the renderer
   * itself stays palette-agnostic so callers control fallbacks.
   */
  spawn(centroid: Vec3, tintColor: number): void {
    const slot = this.pool[this.cursor % POOL_SIZE];
    this.cursor += 1;

    // Reset every per-burst piece of state BEFORE making the slot visible so
    // a recycled slot never renders a frame with stale uniforms / scale /
    // particle positions from the previous animation. Order matters: clear
    // uniforms first, then geometry/transform, then flip `visible`.
    slot.active = true;
    slot.life = 0;
    slot.uProgress.value = 0;
    slot.uColor.value.set(tintColor);

    // Particles fan radially in the XZ plane with a small upward bias so
    // the burst reads as a 3D dome rather than a flat ring.
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const i3 = i * 3;
      slot.positions[i3] = centroid.x;
      slot.positions[i3 + 1] = centroid.y;
      slot.positions[i3 + 2] = centroid.z;

      const theta = (i / PARTICLE_COUNT) * Math.PI * 2;
      const speed = 4.5 + Math.random() * 2.5;
      slot.velocities[i3] = Math.cos(theta) * speed;
      slot.velocities[i3 + 1] = 0.6 + Math.random() * 1.2;
      slot.velocities[i3 + 2] = Math.sin(theta) * speed;
    }
    slot.pointsGeometry.attributes.position.needsUpdate = true;

    slot.ring.position.set(centroid.x, centroid.y + RING_GROUND_OFFSET, centroid.z);
    slot.ring.scale.setScalar(0.001);

    slot.points.visible = true;
    slot.ring.visible = true;
  }

  update(deltaMs: number): void {
    const dt = deltaMs / 1000;
    for (const slot of this.pool) {
      if (!slot.active) continue;
      slot.life += dt;
      const t = slot.life / LIFE_SECONDS;
      if (t >= 1) {
        this.deactivate(slot);
        continue;
      }

      const posAttr = slot.pointsGeometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.85;
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const i3 = i * 3;
        slot.velocities[i3 + 1] -= 3.0 * dt;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + slot.velocities[i3] * dt * drag,
          posAttr.getY(i) + slot.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + slot.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;

      // Ring: ease-out scale 0 → MAX so the wavefront slows visually as
      // it expands. (1 - (1 - t)^2) is a quadratic ease-out.
      const eased = 1 - (1 - t) * (1 - t);
      slot.ring.scale.setScalar(eased * RING_MAX_SCALE);

      slot.uProgress.value = t;
    }
  }

  dispose(): void {
    for (const slot of this.pool) {
      slot.pointsGeometry.dispose();
      slot.pointsMaterial.dispose();
      this.scene.remove(slot.points);

      slot.ring.geometry.dispose();
      slot.ringMaterial.dispose();
      this.scene.remove(slot.ring);
    }
    this.pool.length = 0;
  }

  private createSlot(): BurstSlot {
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const velocities = new Float32Array(PARTICLE_COUNT * 3);
    const pointsGeometry = new THREE.BufferGeometry();
    pointsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(0xffffff));

    const pointsMaterial = new THREE.PointsNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const { sizeNode, colorNode } = buildParticleNodes(uProgress, uColor);
    pointsMaterial.sizeNode = sizeNode;
    pointsMaterial.colorNode = colorNode;
    setEmissiveNode(pointsMaterial, colorNode);

    const points = new THREE.Points(pointsGeometry, pointsMaterial);
    points.visible = false;
    this.scene.add(points);

    // Unit-radius ring; scaled at runtime so RING_MAX_SCALE maps directly
    // to world units.
    const ringGeometry = new THREE.RingGeometry(0.6, 1.0, 64, 1);
    const ringMaterial = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const { colorNode: ringColorNode } = buildRingNodes(uProgress, uColor);
    ringMaterial.colorNode = ringColorNode;
    setEmissiveNode(ringMaterial, ringColorNode);

    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    // Lay flat on the XZ plane (RingGeometry defaults to facing +Z).
    ring.rotation.x = -Math.PI / 2;
    ring.visible = false;
    this.scene.add(ring);

    return {
      points,
      pointsMaterial,
      pointsGeometry,
      positions,
      velocities,
      ring,
      ringMaterial,
      uProgress,
      uColor,
      active: false,
      life: 0,
    };
  }

  private deactivate(slot: BurstSlot): void {
    slot.active = false;
    slot.points.visible = false;
    slot.ring.visible = false;
  }
}
